/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import {
  Typography,
  Switch,
  Paper,
  makeStyles,
  Theme,
  createStyles,
} from "@material-ui/core";
import { EditComponentProps } from "material-table";
import BlockIcon from "@material-ui/icons/Block";
import { WebCrawlingConfigsApi, isValidUrl } from "../../../../helpers";
import {
  EditableTable,
  ColumnBaseHeaderStyle,
  ColumnBaseCellStyle,
  ConfirmDialog,
  ColumnBaseHeaderStyleCenter,
  ColumnBaseCellStyleCenter,
} from "../../../../components";
import {
  UserGroupConfigDataRow,
  WebCrawlingConfigDataRow,
  WebCrawlingConfigDbRow,
} from "../../../../types/config";
import { SiteNameAndUrlRenderComponent } from "./fields/SiteNameAndUrlRenderComponent";
import { SiteNameAndUrlEditComponent } from "./fields/SiteNameAndUrlEditComponent";
import { FiletypeAndScheduleEditComponent } from "./fields/FiletypeAndScheduleEditComponent";
import { FiletypeAndScheduleRenderComponent } from "./fields/FiletypeAndScheduleRenderComponent";
import { WebCrawlingConfigHelper } from "./helpers/web-crawling-config-helper";
import { IsActiveRenderComponent } from "./fields/IsActiveRenderComponent";

/** スタイル定義 */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogPaper: {
      minWidth: "90vh",
      maxWidth: "90vh",
      minHeight: "80vh",
      maxHeight: "80vh",
    },
    dialogTextarea: {
      resize: "none",
      width: "100%",
      height: "62vh",
      fontFamily: theme.typography.fontFamily,
    },
  })
);

interface Props {
  parentData: UserGroupConfigDataRow;
  onChange?: () => void;
  onError?: (error: any) => void;
}

/** Webクロール設定テーブル */
export function WebCrawlingConfigTable(props: Props) {
  const classes = useStyles();

  const { parentData, onChange, onError } = props;

  //------------------------------
  // Webクロール設定
  const [data, setData] = React.useState<WebCrawlingConfigDataRow[]>([]);

  // ユーザーグループIDに紐づく、
  // Webクロール設定（各ノード最新のもの）を取得する
  React.useEffect(() => {
    let cleanedUp = false;

    const fetchData = async () => {
      const userGroupId = parentData.id;
      if (userGroupId) {
        try {
          const res: any = await WebCrawlingConfigsApi.filteredUserGroup(
            userGroupId
          );
          const rows = res.data.map((row: WebCrawlingConfigDbRow) => {
            return WebCrawlingConfigHelper.toDataRow(row);
          });
          if (!cleanedUp) {
            setData(rows);
          }
        } catch (error: any) {
          if (onError) {
            if (!cleanedUp) {
              onError(error);
            }
          }
        }
      }
    };
    fetchData();

    return function cleanup() {
      cleanedUp = true;
    };
  }, []);

  //------------------------------
  // 除外URLダイアログ
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [dialogText, setDialogText] = React.useState<string>("");
  const [dialogDataRow, setDialogDataRow] = React.useState<
    WebCrawlingConfigDataRow | undefined
  >();

  // [Editable] (作成、編集、削除)
  // [Handler]
  // add
  const getRowAddHandler = (
    state: WebCrawlingConfigDataRow[],
    setState: React.Dispatch<WebCrawlingConfigDataRow[]>
  ) => {
    return (newData: any) =>
      new Promise<void>((resolve, reject) => {
        resolve();
        // API Access
        if (parentData.id) {
          const newDbData = WebCrawlingConfigHelper.toDbRow(newData);
          WebCrawlingConfigsApi.create(newDbData)
            // 成功
            .then((res: any) => {
              resolve();
              const createdData = WebCrawlingConfigHelper.toDataRow(res.data);
              // Stateを更新する
              const innerData = [...state];
              innerData.push(createdData);
              setState(innerData);

              // 更新通知
              if (onChange) {
                onChange();
              }
              return true;
            })
            // 失敗
            .catch((error: any) => {
              reject(); // keep open row editable
              if (onError) {
                onError(error);
              }
              return false;
            });
        }
        return true;
      });
  };

  // update
  const getRowUpdateHandler = (
    state: WebCrawlingConfigDataRow[],
    setState: React.Dispatch<WebCrawlingConfigDataRow[]>
  ) => {
    return (
      newData: WebCrawlingConfigDataRow,
      oldData?: WebCrawlingConfigDataRow
    ) =>
      new Promise<void>((resolve, reject) => {
        resolve();
        // API Access
        if (parentData.id && oldData) {
          // Webクロール設定はNodeに紐づき更新履歴を持つため、
          // 同一Nodeに紐づく新規のWebクロール設定を作成する。
          // クライアント側では通常の更新処理と同様に処理する。
          const newDbData = WebCrawlingConfigHelper.toDbRow(newData);
          WebCrawlingConfigsApi.create(newDbData)
            // 成功
            .then((res: any) => {
              resolve();
              const createdData = WebCrawlingConfigHelper.toDataRow(res.data);
              // Stateを更新する
              const innerData = [...state];
              innerData[innerData.indexOf(oldData)] = createdData;
              setState(innerData);

              // 更新通知
              if (onChange) {
                onChange();
              }
              return true;
            })
            // 失敗
            .catch((error: any) => {
              reject(); // keep open row editable
              if (onError) {
                onError(error);
              }
              return false;
            });
        }
        return true;
      });
  };

  // delete
  const getRowDeleteHandler = (
    state: WebCrawlingConfigDataRow[],
    setState: React.Dispatch<WebCrawlingConfigDataRow[]>
  ) => {
    return (oldData: any) =>
      new Promise<void>((resolve, reject) => {
        // API Access
        WebCrawlingConfigsApi.destroy(oldData.id as number)
          //------------------------------
          // 成功
          .then(() => {
            resolve();
            const innerData = [...state];
            innerData.splice(innerData.indexOf(oldData), 1);
            setState(innerData);

            // 更新通知
            if (onChange) {
              onChange();
            }
            return true;
          }) //------------------------------
          // 失敗
          .catch((error: any) => {
            reject(); // keep open row editable
            if (onError) {
              onError(error);
            }
            return false;
          });
      });
  };

  // Validation
  const validate = (values: WebCrawlingConfigDataRow) => {
    const errors: { [k: string]: any } = {};
    if (!values.siteNameAndUrl.siteName) {
      errors.siteName = "入力してください";
    }
    if (!values.siteNameAndUrl.url) {
      errors.url = "入力してください";
    } else if (!isValidUrl(values.siteNameAndUrl.url)) {
      errors.url = "不正なURLです";
    }
    if (!values.maxDepth) {
      errors.maxDepth = "入力してください";
    } else if (!/^([1-9]\d*|0)$/.test(String(values.maxDepth))) {
      errors.maxDepth = "1 - 5 の整数を入力してください";
    } else if (values.maxDepth < 1 || values.maxDepth > 5) {
      errors.maxDepth = "1 - 5 の整数を入力してください";
    }
    return errors;
  };

  //--------------------
  // コンポーネント
  return (
    <div style={{ maxWidth: "100%" }}>
      <EditableTable<WebCrawlingConfigDataRow>
        title={(
          <Typography
            component="h6"
            style={{
              padding: "0.5rem",
              fontSize: "0.8rem",
              fontWeight: "bold",
            }}
          >
          Webクロール設定
          </Typography>
        )}
        columns={[
          {
            title: "有効",
            field: "isActive",
            headerStyle: ColumnBaseHeaderStyle,
            cellStyle: ColumnBaseCellStyle,
            initialEditValue: true,
            width: "5rem",
            customSort: (
              a: WebCrawlingConfigDataRow,
              b: WebCrawlingConfigDataRow
            ) => {
              let result = 0;
              if (a.isActive < b.isActive) {
                result = 1;
              } else if (a.isActive > b.isActive) {
                result = -1;
              }
              return result;
            },
            render: function renderIsActiveColumn(
              rowData: WebCrawlingConfigDataRow
            ) {
              // 表示画面ではスイッチ切替できる
              return (
                <IsActiveRenderComponent
                  rowData={rowData}
                  data={data}
                  setData={setData}
                />
              );
            },
            editComponent: function editComponentIsActiveColumn(
              fieldProps: any
            ) {
              // 編集画面ではスイッチ切替できない
              return (
                <Switch
                  checked={fieldProps.value}
                  color="primary"
                  name="isActive"
                  inputProps={{ "aria-label": "isActive checkbox" }}
                  size="small"
                  disabled
                />
              );
            },
          },
          {
            title: "サイト名 / 基点URL",
            field: "siteNameAndUrl",
            width: "auto",
            headerStyle: ColumnBaseHeaderStyle,
            cellStyle: ColumnBaseCellStyle,
            initialEditValue: "",
            defaultSort: "asc",
            customSort: (
              a: WebCrawlingConfigDataRow,
              b: WebCrawlingConfigDataRow
            ) => {
              const nameA =
                Object.keys(a.siteNameAndUrl).length > 0 &&
                "siteName" in a.siteNameAndUrl
                  ? a.siteNameAndUrl.siteName
                  : "";
              const nameB =
                Object.keys(b.siteNameAndUrl).length > 0 &&
                "siteName" in b.siteNameAndUrl
                  ? b.siteNameAndUrl.siteName
                  : "";
              return nameA.localeCompare(nameB);
            },
            render: function renderComponentSiteNameAndUrl(
              rowData: WebCrawlingConfigDataRow
            ) {
              return <SiteNameAndUrlRenderComponent rowData={rowData} />;
            },
            editComponent: function editComponentSiteNameAndUrl(
              fieldProps: EditComponentProps<WebCrawlingConfigDataRow>
            ) {
              return <SiteNameAndUrlEditComponent {...fieldProps} autoFocus />;
            },
          },
          {
            title: "深度",
            field: "maxDepth",
            type: "numeric",
            width: "4rem",
            sorting: false,
            headerStyle: ColumnBaseHeaderStyleCenter,
            cellStyle: ColumnBaseCellStyleCenter,
            initialEditValue: "",
            defaultSort: "asc",
          },
          {
            title: "ファイル種類 / スケジュール",
            field: "filetypeAndSchedule",
            // width: "22rem",
            sorting: false,
            headerStyle: ColumnBaseHeaderStyle,
            cellStyle: ColumnBaseCellStyle,
            render: function renderFiletypeColumn(
              rowData: WebCrawlingConfigDataRow
            ) {
              return <FiletypeAndScheduleRenderComponent rowData={rowData} />;
            },
            editComponent: function editComponentFiletypeColumn(
              fieldProps: EditComponentProps<WebCrawlingConfigDataRow>
            ) {
              return <FiletypeAndScheduleEditComponent {...fieldProps} />;
            },
          },
        ]}
        data={data}
        initialFormData={{
          userGroup: parentData.id,
          siteNameAndUrl: {
            siteName: "",
            url: "",
          },
          maxDepth: 3,
          excludedUrls: "",
          filetypeAndSchedule: {
            filetype: {
              pdf: true,
              doc: true,
              xls: true,
              ppt: true,
              txt: true,
            },
            schedule: [WebCrawlingConfigHelper.getDefaultSchedule()],
          },
          isActive: true,
        }}
        editable={{
          onRowAdd: getRowAddHandler(data, setData),
          onRowUpdate: getRowUpdateHandler(data, setData),
          onRowDelete: getRowDeleteHandler(data, setData),
        }}
        validate={validate}
        actions={[
          {
            icon: function fileCrawlingTreeActionIcon() {
              return <BlockIcon />;
            },
            tooltip: "除外URL設定",
            onClick: (
              _event: any,
              rowData: WebCrawlingConfigDataRow | WebCrawlingConfigDataRow[]
            ) => {
              if (!("length" in rowData)) {
                // クリックした行のデータを設定してダイアログを開く
                setDialogDataRow(rowData);
                setDialogText(rowData.excludedUrls);
                setDialogOpen(true);
              }
            },
          },
        ]}
        components={{
          Container: function customContainer(componentProps: any) {
            return <Paper elevation={0}>{componentProps.children}</Paper>;
          },
        }}
        options={{
          showTitle: true,
        }}
      />
      <ConfirmDialog
        open={dialogOpen}
        title="除外URL設定"
        dialogClasses={{ paper: classes.dialogPaper }}
        okLabel="　保存　"
        onOk={async () => {
          if (dialogDataRow && dialogDataRow.id) {
            const newDbData = WebCrawlingConfigHelper.toDbRow(dialogDataRow);
            // 入力されている除外URLを設定する
            newDbData.excluded_urls = dialogText;
            WebCrawlingConfigsApi.update(dialogDataRow.id, newDbData)
              // 成功
              .then((res: any) => {
                const updatedData = WebCrawlingConfigHelper.toDataRow(res.data);
                // Stateを更新する
                const innerData = [...data];
                innerData[innerData.indexOf(dialogDataRow)] = updatedData;
                setData(innerData);
                return true;
              })
              // 失敗
              .catch((error: any) => {
                if (onError) {
                  onError(error);
                }
                return false;
              });
          }
          // ダイアログを閉じる
          setDialogOpen(false);
        }}
        onCancel={() => {
          // ダイアログを閉じる
          setDialogOpen(false);
        }}
      >
        <textarea
          className={classes.dialogTextarea}
          value={dialogText}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setDialogText(event.target.value);
          }}
        />
      </ConfirmDialog>
    </div>
  );
}
